import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import {
  ArrowLeft, Loader2, Mail, MapPin, Store, ExternalLink, UtensilsCrossed,
  Boxes, Users, UserCog, ShoppingBag, Gift,
} from "lucide-react";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import api from "@/lib/api";

function Mini({ icon: Icon, label, value }) {
  return (
    <div className="bg-white/5 rounded-lg p-4">
      <Icon className="h-4 w-4 text-stone-400" strokeWidth={1.5} />
      <p className="font-heading text-2xl font-semibold text-white mt-2">{value}</p>
      <p className="text-xs text-stone-500">{label}</p>
    </div>
  );
}

const orderStatusCls = {
  new: "bg-primary/15 text-primary", preparing: "bg-amber-500/15 text-amber-400",
  ready: "bg-emerald-500/15 text-emerald-400", completed: "bg-stone-500/15 text-stone-400",
  cancelled: "bg-red-500/15 text-red-400",
};

export default function AdminClientDetail() {
  const { id } = useParams();
  const [d, setD] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => { api.get(`/admin/clients/${id}`).then((r) => setD(r.data)).catch(() => setErr(true)); }, [id]);

  if (err) return <div className="text-center py-20 text-stone-400">Client not found. <Link to="/admin/clients" className="text-primary">Back</Link></div>;
  if (!d) return <div className="flex justify-center py-20"><Loader2 className="h-7 w-7 animate-spin text-primary" /></div>;

  const money = (n) => `$${Number(n || 0).toLocaleString()}`;
  const sub = d.client.subscription;
  const r = d.restaurant;

  return (
    <div data-testid="admin-client-detail-page">
      <Link to="/admin/clients" className="inline-flex items-center gap-1.5 text-sm text-stone-400 hover:text-white mb-6">
        <ArrowLeft className="h-4 w-4" /> All clients
      </Link>

      <div className="bg-[#1A1814] border border-white/10 rounded-xl p-6 mb-6">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div className="flex items-start gap-4">
            <span className="h-14 w-14 rounded-xl bg-primary/15 flex items-center justify-center font-heading text-2xl font-semibold text-primary">{r?.name?.charAt(0) || "?"}</span>
            <div>
              <h1 className="font-heading text-3xl font-medium text-white">{r?.name || "—"}</h1>
              <p className="text-stone-400">{r?.cuisine}</p>
              <div className="flex flex-wrap gap-4 mt-2 text-sm text-stone-500">
                <span className="flex items-center gap-1.5"><Mail className="h-3.5 w-3.5" /> {d.client.email}</span>
                {r?.address && <span className="flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5" /> {r.address}</span>}
              </div>
            </div>
          </div>
          <div className="text-right">
            <span className={`text-xs px-3 py-1 rounded-full capitalize ${sub.status === "active" ? "bg-emerald-500/15 text-emerald-400" : "bg-amber-500/15 text-amber-400"}`}>
              {sub.status === "active" ? `${sub.plan} · active` : `Trial · ${sub.trial_days_left}d left`}
            </span>
            {r?.slug && (
              <a href={`/m/${r.slug}`} target="_blank" rel="noreferrer" className="block mt-3 text-sm text-primary hover:underline flex items-center justify-end gap-1">
                View public menu <ExternalLink className="h-3.5 w-3.5" />
              </a>
            )}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
        <Mini icon={Store} label="Total Revenue" value={money(d.revenue)} />
        <Mini icon={UtensilsCrossed} label="Menu Items" value={d.counts.menu} />
        <Mini icon={Boxes} label="Inventory" value={d.counts.inventory} />
        <Mini icon={Users} label="Customers" value={d.counts.customers} />
        <Mini icon={UserCog} label="Staff" value={d.counts.staff} />
        <Mini icon={ShoppingBag} label="Orders" value={d.total_orders} />
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-[#1A1814] border border-white/10 rounded-xl p-6">
          <h3 className="text-lg font-medium text-white mb-4">Revenue · last 14 days</h3>
          <div className="h-64" style={{ minHeight: 256 }}>
            <ResponsiveContainer width="100%" height="100%" minHeight={256}>
              <AreaChart data={d.sales_series}>
                <defs>
                  <linearGradient id="arev" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#C86A51" stopOpacity={0.4} />
                    <stop offset="100%" stopColor="#C86A51" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" vertical={false} />
                <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#8a8378" }} tickFormatter={(x) => x?.slice(5)} />
                <YAxis tick={{ fontSize: 11, fill: "#8a8378" }} tickFormatter={(v) => `$${v}`} width={48} />
                <Tooltip formatter={(v) => money(v)} contentStyle={{ borderRadius: 8, border: "1px solid rgba(255,255,255,0.1)", background: "#1A1814", color: "#fff" }} />
                <Area type="monotone" dataKey="amount" stroke="#C86A51" strokeWidth={2} fill="url(#arev)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="space-y-6">
          <div className="bg-[#1A1814] border border-white/10 rounded-xl p-6">
            <h3 className="text-base font-medium text-white mb-3">Staff accounts</h3>
            {d.staff.length === 0 ? <p className="text-sm text-stone-500">No staff accounts.</p> : (
              <div className="space-y-2">
                {d.staff.map((s) => (
                  <div key={s.id} className="flex items-center justify-between text-sm">
                    <span className="text-stone-300">{s.name}</span>
                    <span className="text-stone-500">{s.staff_role}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className="bg-[#1A1814] border border-white/10 rounded-xl p-6">
            <h3 className="text-base font-medium text-white mb-3 flex items-center gap-2"><Gift className="h-4 w-4 text-primary" /> Gift cards ({d.gift_cards.length})</h3>
            {d.gift_cards.length === 0 ? <p className="text-sm text-stone-500">None issued.</p> : (
              <div className="space-y-2">
                {d.gift_cards.slice(0, 5).map((g) => (
                  <div key={g.id} className="flex items-center justify-between text-sm">
                    <span className="font-mono text-xs text-stone-400">{g.code}</span>
                    <span className="text-white">{money(g.balance)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="bg-[#1A1814] border border-white/10 rounded-xl p-6 mt-6">
        <h3 className="text-lg font-medium text-white mb-4">Recent orders</h3>
        {d.recent_orders.length === 0 ? <p className="text-sm text-stone-500">No orders yet.</p> : (
          <div className="divide-y divide-white/5">
            {d.recent_orders.map((o) => (
              <div key={o.id} className="flex items-center justify-between py-3">
                <div>
                  <p className="text-sm text-white">#{o.number} · {o.customer_name}</p>
                  <p className="text-xs text-stone-500 capitalize">{o.order_type}{o.table ? ` · Table ${o.table}` : ""}</p>
                </div>
                <div className="flex items-center gap-3">
                  <span className={`text-xs px-2.5 py-1 rounded-full capitalize ${orderStatusCls[o.status] || ""}`}>{o.status}</span>
                  <span className="text-sm text-white font-medium w-16 text-right">{money(o.total)}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
